/**
 * The general "parts" pattern (OMON-232, Ed, 2026-10-01): a catalogue fact
 * whose `component[]` describes several answers per recording. The ABC chart
 * is the first (A – The Antecedent / B – The Behaviour / C – The Consequence);
 * nothing here knows those names, so a later multi-part fact reuses it as is.
 *
 * Pure (no DOM, no Firestore), so it is testable on its own.
 *
 * The contract, shared with app.carer:
 *   - fact: `component[i] = { code: { coding: [{system, code, display}], text },
 *     permittedDataType: ["string"], _prompt? }` — `_prompt` is our extension
 *     (FHIR R5 `ObservationDefinition.component` has no text of its own);
 *   - plan entry: `_componentPrompts: { [code]: string }`, frozen at add-time
 *     like `_prompt` (ADR-0007), tuned per client;
 *   - recording: `component[]` with `valueString` per answered part, plus a
 *     `note[0].text` summary of `"<label>: <answer>"` lines (the hybrid).
 */

export const COMPANION_SYSTEM = "https://sweettreeconnect.com/fhir/CodeSystem/trackable-companion";

/** The recording kind of the ABC chart (OMON-232). */
export const INCIDENT_KIND = "incident";

export interface EventPart {
  /** The component's code (e.g. "antecedent"). */
  code: string;
  /** What carers and planners read (e.g. "A – The Antecedent"). */
  label: string;
  /** The librarian's default prompt for this part; "" when none. */
  defaultPrompt: string;
}

type Raw = Record<string, unknown>;

function codingOf(component: Raw): Raw | undefined {
  const code = component.code as Raw | undefined;
  const codings = (code?.coding as Raw[] | undefined) ?? [];
  return codings.find((c) => c?.system === COMPANION_SYSTEM) ?? codings[0];
}

/** The parts a fact defines, in order. Anything without a usable code is skipped. */
export function partsOf(od: { component?: unknown } | undefined | null): EventPart[] {
  const raw = od?.component;
  if (!Array.isArray(raw)) return [];
  const out: EventPart[] = [];
  for (const c of raw) {
    if (!c || typeof c !== "object") continue;
    const comp = c as Raw;
    const coding = codingOf(comp);
    const code = typeof coding?.code === "string" ? coding.code : "";
    if (!code) continue;
    const text = (comp.code as Raw | undefined)?.text;
    const label = typeof text === "string" && text.trim()
      ? text
      : typeof coding?.display === "string" && coding.display.trim() ? coding.display : code;
    const defaultPrompt = typeof comp._prompt === "string" ? comp._prompt : "";
    out.push({ code, label, defaultPrompt });
  }
  return out;
}

/** The per-part prompts to freeze onto a new plan entry (add-time snapshot). */
export function seedComponentPrompts(parts: EventPart[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of parts) if (p.defaultPrompt.trim()) out[p.code] = p.defaultPrompt;
  return out;
}

/** What a part's prompt reads as: the plan's own, else the fact's default, else "". */
export function resolvePartPrompt(
  part: EventPart,
  componentPrompts: Record<string, string> | undefined,
): string {
  const own = componentPrompts?.[part.code];
  if (typeof own === "string" && own.trim()) return own;
  return part.defaultPrompt;
}

/**
 * The `_componentPrompts` value to save: only the fact's current parts, only
 * non-empty text. A part left blank is omitted, so readers fall back to the
 * fact's default (the contract), rather than freezing an empty prompt.
 */
export function componentPromptsForSave(
  parts: EventPart[],
  componentPrompts: Record<string, string> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of parts) {
    const v = componentPrompts?.[p.code];
    if (typeof v === "string" && v.trim()) out[p.code] = v.trim();
  }
  return out;
}

/** Normalise a stored `_componentPrompts` (strings only). */
export function readComponentPrompts(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Raw)) if (typeof v === "string") out[k] = v;
  return out;
}

/**
 * The fact's `component[]` with ONE part's `_prompt` replaced (librarian
 * edit). Every other key of every component is carried over untouched; an
 * empty prompt removes the key. Returns a new array.
 */
export function withPartPrompt(component: unknown, code: string, prompt: string): unknown[] {
  const list = Array.isArray(component) ? component : [];
  return list.map((c) => {
    if (!c || typeof c !== "object") return c;
    const comp = c as Raw;
    if (codingOf(comp)?.code !== code) return comp;
    const next: Raw = { ...comp };
    if (prompt.trim()) next._prompt = prompt.trim();
    else delete next._prompt;
    return next;
  });
}

/** An incident (ABC) entry has no target and no escalation threshold. */
export function isIncidentKind(kind: string | undefined): boolean {
  return kind === INCIDENT_KIND;
}
